(() => {
  const initializeMenuToggles = (root = document) => {
    const menuToggles = root.querySelectorAll('[data-menu-toggle]');

    menuToggles.forEach((toggle) => {
      if (toggle.dataset.menuReady === 'true') return;

      const navigationId = toggle.getAttribute('aria-controls');
      const navigation = navigationId ? document.getElementById(navigationId) : null;

      if (!navigation) return;

      toggle.dataset.menuReady = 'true';
      toggle.addEventListener('click', () => {
        const isOpen = navigation.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(isOpen));
        toggle.setAttribute('aria-label', isOpen ? 'Close navigation' : 'Open navigation');
      });
    });
  };

  const initializeHeroSlideshows = (root = document) => {
    const slideshows = [];

    if (root.matches?.('[data-hero-slideshow]')) {
      slideshows.push(root);
    }

    root.querySelectorAll?.('[data-hero-slideshow]').forEach((slideshow) => {
      slideshows.push(slideshow);
    });

    slideshows.forEach((slideshow) => {
      if (slideshow.dataset.heroReady === 'true') return;

      const slides = Array.from(slideshow.querySelectorAll('[data-hero-slide]'));
      const controls = slideshow.querySelector('[data-hero-controls]');
      const toggle = slideshow.querySelector('[data-hero-toggle]');
      const toggleIcon = slideshow.querySelector('[data-hero-toggle-icon]');
      const toggleLabel = slideshow.querySelector('[data-hero-toggle-label]');
      const previous = slideshow.querySelector('[data-hero-prev]');
      const next = slideshow.querySelector('[data-hero-next]');
      const dots = Array.from(slideshow.querySelectorAll('[data-hero-dot]'));
      const status = slideshow.querySelector('[data-hero-status]');
      const motionPreference = window.matchMedia
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : { matches: false };
      const autoRotate = slideshow.dataset.autoplay === 'true';
      const interval = Math.max(Number.parseInt(slideshow.dataset.interval || '7000', 10), 5000);

      if (!slides.length) return;

      let currentIndex = Math.max(
        slides.findIndex((slide) => slide.classList.contains('is-active')),
        0
      );
      let timerId;
      let pointerPaused = false;
      let focusPaused = false;
      let rotationPaused = motionPreference.matches;
      let mediaPaused = motionPreference.matches;

      const clearTimer = () => {
        if (!timerId) return;
        window.clearTimeout(timerId);
        timerId = undefined;
      };

      const updateToggle = () => {
        if (!toggle) return;

        const isPaused = mediaPaused || (autoRotate && slides.length > 1 && rotationPaused);
        const label = isPaused ? 'Play slideshow' : 'Pause slideshow';
        toggle.setAttribute('aria-label', label);

        if (toggleLabel) toggleLabel.textContent = label;
        if (toggleIcon) toggleIcon.textContent = isPaused ? '▶' : '❚❚';
      };

      const syncVideos = () => {
        slides.forEach((slide, index) => {
          const video = slide.querySelector('video');

          if (!video) return;

          video.muted = true;

          if (index === currentIndex && !mediaPaused && !document.hidden) {
            const playRequest = video.play();
            playRequest?.catch(() => {});
            return;
          }

          video.pause();

          if (index !== currentIndex && video.currentTime) {
            try {
              video.currentTime = 0;
            } catch (_error) {
              // The browser may not have loaded enough metadata to seek yet.
            }
          }
        });
      };

      const scheduleNextSlide = () => {
        clearTimer();

        if (
          slides.length < 2 ||
          !autoRotate ||
          rotationPaused ||
          pointerPaused ||
          focusPaused ||
          document.hidden
        ) {
          return;
        }

        timerId = window.setTimeout(() => {
          showSlide(currentIndex + 1, false);
        }, interval);
      };

      const showSlide = (requestedIndex, announceChange = false) => {
        currentIndex = (requestedIndex + slides.length) % slides.length;

        slides.forEach((slide, index) => {
          const isActive = index === currentIndex;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', String(!isActive));
        });

        dots.forEach((dot, index) => {
          dot.setAttribute('aria-current', String(index === currentIndex));
        });

        if (announceChange && status) {
          status.textContent = `Slide ${currentIndex + 1} of ${slides.length}`;
        }

        syncVideos();
        scheduleNextSlide();
      };

      const setPaused = (paused) => {
        if (autoRotate) rotationPaused = paused;
        mediaPaused = paused;
        updateToggle();
        syncVideos();
        scheduleNextSlide();
      };

      const showManualSlide = (requestedIndex) => {
        if (autoRotate) rotationPaused = true;
        updateToggle();
        showSlide(requestedIndex, true);
      };

      toggle?.addEventListener('click', () => {
        const isPaused = mediaPaused || (autoRotate && slides.length > 1 && rotationPaused);
        setPaused(!isPaused);
      });

      previous?.addEventListener('click', () => {
        showManualSlide(currentIndex - 1);
      });

      next?.addEventListener('click', () => {
        showManualSlide(currentIndex + 1);
      });

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          showManualSlide(Number.parseInt(dot.dataset.heroDot || '0', 10));
        });
      });

      slideshow.addEventListener('mouseenter', () => {
        pointerPaused = true;
        clearTimer();
      });

      slideshow.addEventListener('mouseleave', () => {
        pointerPaused = false;
        scheduleNextSlide();
      });

      slideshow.addEventListener('focusin', () => {
        focusPaused = true;
        clearTimer();
      });

      slideshow.addEventListener('focusout', () => {
        window.requestAnimationFrame(() => {
          focusPaused = slideshow.contains(document.activeElement);
          scheduleNextSlide();
        });
      });

      slideshow.addEventListener('shopify:block:select', (event) => {
        const selectedSlide = event.target.closest?.('[data-hero-slide]');
        const selectedIndex = selectedSlide ? slides.indexOf(selectedSlide) : -1;

        if (selectedIndex < 0) return;

        setPaused(true);
        showSlide(selectedIndex, false);
      });

      document.addEventListener('visibilitychange', () => {
        if (!slideshow.isConnected) {
          clearTimer();
          return;
        }

        syncVideos();
        scheduleNextSlide();
      });

      motionPreference.addEventListener?.('change', (event) => {
        if (event.matches) setPaused(true);
      });

      slideshow.dataset.heroReady = 'true';
      updateToggle();
      showSlide(currentIndex, false);

      if (controls) controls.hidden = false;
    });
  };

  initializeMenuToggles();
  initializeHeroSlideshows();

  document.addEventListener('shopify:section:load', (event) => {
    initializeMenuToggles(event.target);
    initializeHeroSlideshows(event.target);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('.quick-add-form');

    if (!form || !window.fetch || !window.FormData) return;

    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    const cartAddUrl = document.body.dataset.cartAddUrl;
    const cartUrl = document.body.dataset.cartUrl;
    const defaultText = button?.dataset.defaultText || button?.textContent || 'Add to cart';
    const addedText = button?.dataset.addedText || 'Added';

    if (!button || !cartAddUrl || !cartUrl) {
      form.submit();
      return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${cartAddUrl}.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new FormData(form)
      });

      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload.description || 'This item could not be added to the cart.');
      }

      const cartResponse = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();

      document.querySelectorAll('[data-cart-count]').forEach((count) => {
        count.textContent = String(cart.item_count);
      });

      button.textContent = addedText;
      window.setTimeout(() => {
        button.textContent = defaultText;
      }, 1200);
    } catch (error) {
      let message = form.querySelector('.quick-add-error');

      if (!message) {
        message = document.createElement('p');
        message.className = 'quick-add-error';
        message.setAttribute('role', 'alert');
        form.appendChild(message);
      }

      message.textContent = error instanceof Error ? error.message : 'This item could not be added to the cart.';
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  });
})();
